import { Component, EventEmitter, inject, Input, LOCALE_ID, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core'
import { ControlContainer, NgForm, FormsModule } from '@angular/forms'
import { forkJoin, Subscription } from 'rxjs'

import { Select } from 'primeng/select'
import { DetailMode } from '@app/components/generic/generic.constants'
import { CountriesService } from '@app/generated/api/countries/countries.service'
import type { CountriesListParams, CountryListItem } from '@app/generated/api/model'

@Component({
  selector: 'app-country-field',
  templateUrl: 'country-field.component.html',
  viewProviders: [{
    provide: ControlContainer,
    deps: [[Optional, NgForm]],
    useFactory: (ngForm: NgForm) => ngForm,
  }],
  imports: [
    Select,
    FormsModule,
  ],
})
export class CountryFieldComponent implements OnInit, OnChanges {
  public readonly countriesService = inject(CountriesService)
  public readonly locale = inject(LOCALE_ID)

  @Input() country?: Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | null
  @Input() required: boolean = false
  @Input() mode: string = 'normal'
  @Input() tabindex: number = -1

  @Output() countryChange = new EventEmitter<Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | null>()

  // there are 3 lists of countries:
  // - one for reading (contains only countries the user has the right to read from)
  // - one for writing (contains only countries the user has the right to read from + write to)
  // - one for the actual display, whose content alternates between the above 2 as a function of the form mode
  public writeCountries: CountryListItem[] = []
  public readCountries: CountryListItem[] = []
  public countries: CountryListItem[] = []

  public filterLabelField: string = 'label_en'
  protected readonly DetailMode = DetailMode

  ngOnInit (): void {
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }

    this.loadCountries().add(() => {
      // once the countries are loaded, if we're in edit mode and we only have 1 country to choose from,
      // then automatically select it from the list.
      if (this.mode === 'edition') {
        if (this.countries.length === 1) {
          this.country = this.countries[0]
          this.countryChange.emit(this.country)
        }
      }
    })
  }

  ngOnChanges (changes: SimpleChanges<CountryFieldComponent>) {
    // detect that the form is in read or write mode and set the field offices lists accordingly.
    if (changes.mode) {
      this.countries = this.getCountriesList()
    }
  }

  /**
   * return the list of Countries according to the current mode.
   * If we are in 'normal' mode, return all the Countries that exist, in case we are visualising a document for which we
   * don't have a UserConfiguration entry for.
   * Again, is this a real use case ? if not we can delete this and return always the `userCountries`.
   */
  private getCountriesList (): CountryListItem[] {
    // if we're in edit mode, only present the list of countries the user can assign to documents
    if (this.mode === 'edition') {
      return this.writeCountries
    }
    // otherwise we're in read mode, use the full list
    return this.readCountries
  }

  /**
   * Load the total list as well as the user-specific countries for the drop-down list.
   */
  loadCountries (): Subscription {
    const readParams: CountriesListParams = { archives_user_can_read: true, ordering: 'label_en', is_acted: true }
    const writeParams: CountriesListParams = { archives_user_can_write: true, ordering: 'label_en', is_acted: true }
    const readCountriesObservable = this.countriesService.list(readParams)
    const writeCountriesObservable = this.countriesService.list(writeParams)
    return forkJoin([readCountriesObservable, writeCountriesObservable]).subscribe((combinedResults) => {
      this.readCountries = combinedResults[0].results
      this.writeCountries = combinedResults[1].results
      this.countries = this.getCountriesList()
    })
  }
}
